import React, { ReactElement, useEffect } from 'react';
import { Flex, VStack } from '@cad-arc';
import { noop, isEqual } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { EsriMapMultiselectField } from './esri-map-multiselect-field';
import { EsriMapFilterOptions, EsriMapFilterState } from '../types';
import { useApplicationType } from '../../common/hooks/use-application-type';

type EsriMapFiltersDropdownProps = {
    filterOptions: EsriMapFilterOptions;
    filterState: EsriMapFilterState;
    setFilterState: (filterState: EsriMapFilterState) => void;
};

export const EsriMapFiltersDropdown = ({
    filterOptions,
    filterState,
    setFilterState,
}: EsriMapFiltersDropdownProps): ReactElement => {
    const { isFirstResponder } = useApplicationType();
    const {
        priorityOptions,
        dispatchAreaOptions,
        unitStatusOptions,
        subdivisionOptions,
        primarySubdivisionName,
        agencyTypeOptions,
        agencyProfileOptions,
    } = filterOptions;

    const methods = useForm({
        defaultValues: filterState,
    });

    const { watch } = methods;
    const formStateWatch = watch();

    useEffect(() => {
        if (!isEqual(filterState, formStateWatch)) {
            setFilterState(formStateWatch);
        }
    }, [formStateWatch, filterOptions, filterState, setFilterState]);

    return (
        <Flex
            backgroundColor="surface.foreground"
            width="280px"
            flexDirection="column"
            alignItems="flex-start"
            position="relative"
            data-testid="MAP_FILTERS_DROPDOWN"
        >
            <FormProvider {...methods}>
                <form onSubmit={noop} style={{ width: '100%' }}>
                    <VStack spacing="20px">
                        {priorityOptions?.length && (
                            <EsriMapMultiselectField
                                label="Priority"
                                name="priorityFilters"
                                options={priorityOptions}
                            />
                        )}
                        {unitStatusOptions?.length && (
                            <EsriMapMultiselectField
                                label="Unit Status"
                                name="mapUnitStatusFilters"
                                options={unitStatusOptions}
                            />
                        )}
                        {subdivisionOptions?.length && (
                            <EsriMapMultiselectField
                                label={primarySubdivisionName ?? 'Beat'}
                                name="primarySubdivisionFilters"
                                options={subdivisionOptions}
                            />
                        )}
                        {dispatchAreaOptions?.length && (
                            <EsriMapMultiselectField
                                label="Dispatch Area"
                                name="dispatchAreaFilters"
                                options={dispatchAreaOptions}
                            />
                        )}
                        {isFirstResponder && agencyTypeOptions?.length && (
                            <EsriMapMultiselectField
                                label="Agency Class"
                                name="agencyTypeFilters"
                                options={agencyTypeOptions}
                            />
                        )}
                        {agencyProfileOptions?.length && (
                            <EsriMapMultiselectField
                                label="Agency Name"
                                name="agencyProfileFilters"
                                options={agencyProfileOptions}
                            />
                        )}
                    </VStack>
                </form>
            </FormProvider>
        </Flex>
    );
};
